// Hand Advisor — pure analysis for a real-life table. Given the tiles a
// player is physically holding (concealed + any exposed sets), rank the 2026
// card hands they are closest to, say which tiles to keep for each, which
// tiles are still needed, and recommend a discard (or three Charleston
// passes). No DOM, no randomness, no timestamps — Deno-safe like the rest of
// the engine.
//
// Fit score = matched real tiles + usable jokers, out of 14 (see handFit).

import { HANDS } from './cards/nmjl2026.js';
import { handFit, validateWin } from './handMatch.js';
import { discardScores } from './ai.js';
import { tileKey } from './tiles.js';

// Copies of each tile in a full set — used to flag needs that can't be met.
export const COPIES = { flower: 8, joker: 8 };
export const COPIES_DEFAULT = 4;

export function keyToName(k) {
  if (k === 'flower') return 'Flower';
  if (k === 'joker') return 'Joker';
  if (k === 'dragon_white') return 'White Dragon / 0';
  if (k.startsWith('dragon_')) return k.slice(7)[0].toUpperCase() + k.slice(8) + ' Dragon';
  if (k.startsWith('wind_')) return k.slice(5)[0].toUpperCase() + k.slice(6) + ' Wind';
  const m = /^(crak|bam|dot)(\d)$/.exec(k);
  if (m) return `${m[2]} ${m[1][0].toUpperCase() + m[1].slice(1)}`;
  return k;
}

// Split `tiles` against one target's need map. Returns which tiles the hand
// uses (keep), which are surplus (toss), and what is still missing. Jokers
// are always kept — they fill any pung+ slot and are never worth discarding.
export function splitForTarget(tiles, target) {
  const used = new Map();
  const keep = [];
  const toss = [];
  let jokers = 0;
  for (const t of tiles) {
    if (t.type === 'joker') { jokers++; keep.push(t); continue; }
    const k = tileKey(t);
    const want = target.need.get(k) || 0;
    const u = used.get(k) || 0;
    if (u < want) { used.set(k, u + 1); keep.push(t); } else toss.push(t);
  }
  // Slots a joker may fill: any key required 3+ times. Jokers cover the
  // shortfall in those groups first (they can't touch singles/pairs).
  const missing = [];
  let jokerBudget = jokers;
  let missingTotal = 0;
  for (const [k, n] of target.need) {
    let short = n - (used.get(k) || 0);
    if (short <= 0) continue;
    if (n >= 3 && jokerBudget > 0) {
      const fill = Math.min(short, jokerBudget);
      jokerBudget -= fill;
      short -= fill;
    }
    if (short > 0) {
      const max = COPIES[k] || COPIES_DEFAULT;
      const held = used.get(k) || 0;
      missing.push({ key: k, name: keyToName(k), count: short, jokerOk: n >= 3, left: Math.max(0, max - held) });
      missingTotal += short;
    }
  }
  missing.sort((a, b) => b.count - a.count);
  return { keep, toss, missing, missingTotal, spareJokers: jokerBudget };
}

/**
 * Analyze a real-table hand.
 * @param tiles      every tile the player holds (concealed + exposed), 13 or 14
 * @param opts.hasExposures  true once any set has been called/exposed — rules
 *                           out concealed (C) hands
 * @param opts.top   how many candidate hands to return
 * @returns { targets, discard, discardOrder, charlestonPass, win, count }
 */
export function adviseHand(tiles, { hasExposures = false, top = 5 } = {}) {
  const count = tiles.length;
  const ranked = HANDS
    .map((hand) => handFit(tiles, hand, hasExposures))
    .filter((f) => f.score >= 0)
    .sort((a, b) => b.score - a.score || b.hand.value - a.hand.value);

  const targets = ranked.slice(0, top).map((fit, i) => {
    const split = splitForTarget(tiles, fit);
    return {
      rank: i + 1,
      hand: fit.hand,
      id: fit.hand.id,
      label: fit.hand.label,
      desc: fit.hand.desc,
      section: fit.hand.section,
      value: fit.hand.value,
      concealed: fit.hand.concealed,
      score: fit.score,
      need: fit.need,
      ...split,
    };
  });

  // Discard order blends the top three targets (weights 4/2/1, same as the
  // Advanced AI) so the suggestion keeps tiles with backup value.
  const player = { hand: tiles, exposed: hasExposures ? [{ tiles: [] }] : [] };
  const scored = tiles.length ? discardScores(player, true) : [];
  const discardOrder = scored.slice().sort((a, b) => a.keep - b.keep).map((s) => s.t);
  const discard = discardOrder.length ? discardOrder[0] : null;
  const charlestonPass = discardOrder.filter((t) => t.type !== 'joker').slice(0, 3);

  const win = count === 14 ? validateWin(tiles, hasExposures) : null;
  return { count, targets, discard, discardOrder, charlestonPass, win };
}

// Short reason for a discard, for the UI.
export function explainDiscard(tile, targets) {
  if (!tile) return '';
  if (tile.type === 'joker') return 'Never discard a joker.';
  const k = tile.type === 'flower' ? 'flower' : tileKey(tile);
  const wanted = targets.filter((t) => (t.need.get(k) || 0) > 0);
  if (!wanted.length) return `None of your closest hands use ${keyToName(k)}.`;
  const dup = targets[0] && targets[0].toss.includes(tile);
  if (dup) return `An extra copy — your best hand already has the ${keyToName(k)}s it needs.`;
  return `Only helps a weaker backup hand.`;
}
